import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Add as AddIcon,
	ArrowUpward as ArrowUpwardIcon,
	ArrowDownward as ArrowDownwardIcon,
	Check as CheckIcon,
	Close as CloseIcon,
	Delete as DeleteIcon,
	DragIndicator as DragIndicatorIcon,
	Download as DownloadIcon,
	Edit as EditIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	Search as SearchIcon,
	UnfoldMore as UnfoldMoreIcon,
	UnfoldLess as UnfoldLessIcon,
	ChevronRight as ChevronRightIcon,
	Upload as UploadIcon,
} from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	Container,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	IconButton,
	InputAdornment,
	OutlinedInput,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useTheme,
	useMediaQuery
} from '@mui/material';
import { Alert, Snackbar } from '@mui/material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { useNavigate, useSearchParams as useUrlSearchParams } from 'react-router-dom';
import SearchInput, { SearchInputHandle } from '../../../components/SearchInput';
import * as XLSX from 'xlsx';
import { useNeoConfirm } from '../../../hooks/useNeoConfirm';
import i18next, {getBrowserLanguage} from '../../../lang';
import { useStores } from '../../../stores/useStores';
import { Category, Product } from './Interfaces';
import { ProductSimpleTableVertical } from './ProductSimpleTableVertical';
import SortableTable, { SortableTableColumn } from '../../../components/SortableTable';
import { ExcelProduct, ProductUploadConfirmDialog } from './ProductUploadConfirmDialog';
import ConfirmDialog from "../../../components/ConfirmDialog";
import MobileCardView, { MobileCardField } from '../../../components/MobileCardView';
import { adminStyles } from '../../../theme/adminStyles';

// 行内編集の入力欄で共通に使うスタイル。
// MUI 既定の左右パディング14pxは列幅の狭いテーブルでは大きすぎ、
// 数文字しか見えなくなるため6pxまで詰めて表示文字数を稼ぐ。
const EDIT_INPUT_SX = {
	fontSize: '0.8rem',
	'& .MuiOutlinedInput-input': { px: '6px' },
} as const;

// ドラッグ＆ドロップ対応モバイルカード
const SortableMobileCard: React.FC<{
	row: Product;
	isSelected: boolean;
	onSelectRow: (id: number) => void;
	onDelete: (row: any) => void;
	onNavigate: (id: number) => void;
}> = ({ row, isSelected, onSelectRow, onDelete, onNavigate }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};
	return (
		<Paper
			ref={setNodeRef}
			style={style}
			elevation={0}
			sx={{
				ml: 2, mt: 0.5, p: 1.5,
				border: '1px solid',
				borderColor: isSelected ? 'primary.main' : 'divider',
				borderRadius: '8px',
				backgroundColor: isSelected ? '#FFF3E0' : '#fff',
				display: 'flex',
				alignItems: 'flex-start',
				gap: 0.5,
			}}
		>
			<Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', pt: '2px' }} {...attributes} {...listeners}>
				<DragIndicatorIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }} onClick={() => onNavigate(row.id)}>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
					<Checkbox
						size="small"
						checked={isSelected}
						onClick={(e) => { e.stopPropagation(); onSelectRow(row.id); }}
						sx={{ p: '2px', mr: 0.5, mt: '-2px' }}
					/>
					<Typography variant="body2" sx={{ flex: 1 }}>
						{row.volume ? `${row.volume} ` : ''}{row.title}
					</Typography>
					<Typography variant="caption" sx={{
						ml: 1, px: 1, py: 0.25, borderRadius: '4px',
						bgcolor: row.status === 'ACTIVE' ? 'success.light' : 'grey.300',
						color: row.status === 'ACTIVE' ? 'success.dark' : 'text.secondary',
						whiteSpace: 'nowrap',
					}}>
						{row.status === 'ACTIVE' ? i18next.t('common.available_for_sale') : row.status === 'INACTIVE' ? i18next.t('common.private') : i18next.t('common.status_deleted')}
					</Typography>
				</Box>
				<Box sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<span>{row.code || ''}</span>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<span style={{ fontWeight: 600 }}>{i18next.t('common.price_yen', { price: row.price.toLocaleString() })}</span>
						<IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(row); }} sx={{ p: '2px' }}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};

// ドラッグ＆ドロップ対応テーブル行
const SortableProductRow: React.FC<{
	row: Product;
	isEditing: boolean;
	isSelected: boolean;
	visibleColumns: SortableTableColumn<Product>[];
	onSelectRow: (id: number) => void;
	onRowChange: (updated: Product) => void;
}> = ({ row, isEditing, isSelected, visibleColumns, onSelectRow, onRowChange }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};
	return (
		<TableRow
			ref={setNodeRef}
			style={style}
			hover
			selected={isSelected}
			sx={{
				'& .MuiTableCell-root': { py: '6px', px: '8px', fontSize: '0.8rem' },
				...(isEditing ? { bgcolor: '#fdd', '& .MuiTableCell-root': { bgcolor: '#fdd' } } : {}),
			}}
		>
			<TableCell sx={{ width: 30, p: 0, textAlign: 'center', cursor: 'grab' }} {...attributes} {...listeners}>
				<DragIndicatorIcon sx={{ fontSize: '1rem', color: 'text.secondary', verticalAlign: 'middle' }} />
			</TableCell>
			<TableCell padding="checkbox">
				<Checkbox
					size="small"
					checked={isSelected}
					onClick={(e) => e.stopPropagation()}
					onChange={() => onSelectRow(row.id)}
				/>
			</TableCell>
			{visibleColumns.map((col) => {
				const cellAlign = col.align || 'center';
				const content = isEditing && col.renderEditCell
					? col.renderEditCell(row, (updated) => onRowChange(updated))
					: col.renderCell
						? col.renderCell(row)
						: (row as any)[col.key];
				return (
					<TableCell
						key={col.key}
						align={cellAlign}
						sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
					>
						{content}
					</TableCell>
				);
			})}
		</TableRow>
	);
};

const ProductManagement: React.FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const navigate = useNavigate();
	const { viewStatusStore } = useStores();
	const { confirm, alert } = useNeoConfirm();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [products, setProducts] = useState<Product[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);

	// URL クエリと同期 (戻るボタンで検索状態を復元するため)
	const [urlParams, setUrlParams] = useUrlSearchParams();
	const [searchParams, setSearchParams] = useState({
		categoryId: '0',
		targetGrade: '',
		keyword: urlParams.get('keyword') || '',
	});
	const [sortColumn, setSortColumn] = useState<string | null>(urlParams.get('sort') || null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
		(urlParams.get('dir') === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
	);

	const syncToUrl = useCallback((overrides?: {
		keyword?: string;
		sortColumn?: string | null;
		sortDirection?: 'asc' | 'desc';
	}) => {
		const next = new URLSearchParams();
		const keyword = overrides?.keyword !== undefined ? overrides.keyword : searchParams.keyword;
		const sCol = overrides?.sortColumn !== undefined ? overrides.sortColumn : sortColumn;
		const sDir = overrides?.sortDirection !== undefined ? overrides.sortDirection : sortDirection;

		if (keyword) next.set('keyword', keyword);
		if (sCol) next.set('sort', sCol);
		if (sCol && sDir !== 'asc') next.set('dir', sDir);

		setUrlParams(next, { replace: true });
	}, [searchParams.keyword, sortColumn, sortDirection, setUrlParams]);
	const [excelProducts, setExcelProducts] = useState<ExcelProduct[]>([]);
	const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
	const [uploadError, setUploadError] = useState<string>("");
	const [isDeleteFailDialogOpen, setIsDeleteFailDialogOpen] = useState(false);
	const [deleteFailMessage, setDeleteFailMessage] = useState<string>("");
	const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
	const [isBulkEditDialogOpen, setIsBulkEditDialogOpen] = useState(false);
	const [bulkEditFields, setBulkEditFields] = useState({
		curriculumName: { enabled: false, value: '' },
		volume: { enabled: false, value: '' },
		title: { enabled: false, value: '' },
		price: { enabled: false, value: '' },
		status: { enabled: false, value: '' },
	});

	const openBulkEditDialog = () => {
		const selectedProducts = editRows.filter(r => selectedRowIds.has(r.id));
		const getCommonOrMixed = (field: keyof Product) => {
			const values = selectedProducts.map(p => String((p as any)[field] ?? ''));
			const unique = [...new Set(values)];
			return unique.length === 1 ? unique[0] : i18next.t('common.mixed_values');
		};
		setBulkEditFields({
			curriculumName: { enabled: false, value: getCommonOrMixed('curriculumName') },
			volume: { enabled: false, value: getCommonOrMixed('volume') },
			title: { enabled: false, value: getCommonOrMixed('title') },
			price: { enabled: false, value: getCommonOrMixed('price') },
			status: { enabled: false, value: getCommonOrMixed('status') },
		});
		setIsBulkEditDialogOpen(true);
	};

	const handleBulkEditSave = async () => {
		if (bulkEditFields.price.enabled && Number(bulkEditFields.price.value) < 0) {
			alert(i18next.t('product.price_must_be_zero_or_positive'));
			return;
		}
		setIsBulkEditDialogOpen(false);
		const selectedProducts = editRows.filter(r => selectedRowIds.has(r.id));

		viewStatusStore.setProgressStatus({
			open: true,
			progress: 0,
			total: selectedProducts.length,
			messageFunc: (progress: number, total: number) => `${progress}/${total}`,
			title: i18next.t('common.bulk_edit'),
		});

		try {
			let progressed = 0;
			const promises = selectedProducts.map(async (product) => {
				const updated = { ...product };
				if (bulkEditFields.curriculumName.enabled) updated.curriculumName = bulkEditFields.curriculumName.value;
				if (bulkEditFields.volume.enabled) updated.volume = bulkEditFields.volume.value;
				if (bulkEditFields.title.enabled) updated.title = bulkEditFields.title.value;
				if (bulkEditFields.price.enabled) updated.price = Number(bulkEditFields.price.value) || 0;
				if (bulkEditFields.status.enabled) updated.status = bulkEditFields.status.value as Product['status'];

				try {
					await axios.put(`/api/v1/admin/products/${product.id}`, {
						categoryId: product.categoryId,
						curriculumName: updated.curriculumName,
						volume: updated.volume,
						title: updated.title,
						price: updated.price,
						code: product.code,
						status: updated.status,
					});
				} finally {
					viewStatusStore.setProgressStatus({
						open: true,
						progress: ++progressed,
						total: selectedProducts.length,
						messageFunc: (progress: number, total: number) => `${progress}/${total}`,
						title: i18next.t('common.bulk_edit'),
					});
				}
			});
			await Promise.all(promises);
			fetchProducts();
		} catch (error) {
			console.error('Failed to bulk edit products:', error);
		} finally {
			viewStatusStore.setProgressStatus({
				open: false, progress: 0, total: 0,
				messageFunc: (progress: number, total: number) => `${progress}/${total}`,
				title: "",
			});
		}
	};

	useEffect(() => {
		fetchProducts();
		fetchCategories();
	}, []);

	useEffect(() => {
		fetchProducts();
	}, [sortColumn, sortDirection]);

	const fetchProducts = async () => {
		try {
			const params = new URLSearchParams();
			if (searchParams.categoryId) params.append('categoryId', searchParams.categoryId);
			if (searchParams.keyword) params.append('keyword', searchParams.keyword);
			if (sortColumn) {
				params.append('sortColumn', sortColumn);
				params.append('sortDirection', sortDirection);
			}

			const response = await axios.get('/api/v1/admin/products', { params });
			setProducts(response.data);
		} catch (error) {
			console.error('Failed to fetch products:', error);
		}
	};

	const fetchCategories = async () => {
		try {
			const response = await axios.get('/api/v1/admin/product-categories');
			setCategories(response.data);
		} catch (error) {
			console.error('Failed to fetch categories:', error);
		}
	};

	const searchInputRef = useRef<SearchInputHandle>(null);

	const handleSearch = (keyword?: string) => {
		const k = keyword !== undefined ? keyword : (searchInputRef.current?.getValue() ?? searchParams.keyword);
		const next = { ...searchParams, keyword: k };
		setSearchParams(next);
		syncToUrl({ keyword: k });
		// fetchProducts は state 反映後の re-render を待つため、直接呼ぶ場合は引数を渡す
		fetchProductsWith(k);
	};

	const fetchProductsWith = async (keyword: string) => {
		try {
			const params = new URLSearchParams();
			if (searchParams.categoryId) params.append('categoryId', searchParams.categoryId);
			if (keyword) params.append('keyword', keyword);
			if (sortColumn) {
				params.append('sortColumn', sortColumn);
				params.append('sortDirection', sortDirection);
			}
			const response = await axios.get('/api/v1/admin/products', { params });
			setProducts(response.data);
		} catch (error) {
			console.error('Failed to fetch products:', error);
		}
	};

	const handleSort = (columnKey: string) => {
		let newCol: string | null;
		let newDir: 'asc' | 'desc';
		if (sortColumn === columnKey) {
			if (sortDirection === 'asc') {
				newCol = columnKey;
				newDir = 'desc';
			} else {
				newCol = null;
				newDir = 'asc';
			}
		} else {
			newCol = columnKey;
			newDir = 'asc';
		}
		setSortColumn(newCol);
		setSortDirection(newDir);
		syncToUrl({ sortColumn: newCol, sortDirection: newDir });
	};

	const handleDelete = async (row: ExcelProduct) => {
		const excelProducts = [row];
		excelProducts.forEach(product => {
			product.categoryName = (product as any).category.name;
		});

		const result = await confirm(
			(<ProductSimpleTableVertical products={excelProducts} fontSize='0.8rem' />),
			(<>
				{i18next.t('product.confirm_delete_product')}
			</>),
		);

		if (result) {
			try {
				await axios.delete(`/api/v1/admin/products/${row.id}`);
				fetchProducts();

				await alert(
					(<ProductSimpleTableVertical products={excelProducts} fontSize='0.8rem' />),
					(<> {i18next.t('common.deleted_success')} </>),
				);

			} catch (error) {
				if (error.response.data.errorCode === 'BadRequest') {
					setDeleteFailMessage(prev => `${prev}\n${error.response.data.message}`);
					setIsDeleteFailDialogOpen(true);
				}
			}
		}
	};

	const handleDeleteSelected = async () => {
		viewStatusStore.setProgressStatus({
			open: true,
			progress: 0,
			total: excelProducts.length,
			messageFunc: (progress: number, total: number) => `${progress}/${total}`,
			title: i18next.t('common.deleting'),
		});

		try {
			let progressed = 0;
			const arr = Array.from(selectedRowIds);
			setDeleteFailMessage("");
			const promises = arr.map(async (id) => {
				try {
					await axios.delete(`/api/v1/admin/products/${id}`);
				} catch (error) {
					if (error.response.data.errorCode === 'BadRequest') {
						setDeleteFailMessage(prev => `${prev}\n${error.response.data.message}`);
						setIsDeleteFailDialogOpen(true);
					}
				} finally {
					viewStatusStore.setProgressStatus({
						open: true,
						progress: ++progressed,
						total: selectedRowIds.size,
						messageFunc: (progress: number, total: number) => `${progress}/${total}`,
						title: i18next.t('common.deleting'),
					});
				}
			});
			await Promise.all(promises);
			setSelectedRowIds(new Set());
			console.log("fetchProducts");
			fetchProducts();

		} catch (error) {
			console.error('Failed to delete products:', error);
		} finally {
			viewStatusStore.setProgressStatus({
				open: false,
				progress: 0,
				total: 0,
				messageFunc: (progress: number, total: number) => `${progress}/${total}`,
				title: "",
			});

		}
	};

	const validateProduct = (product: ExcelProduct): string[] => {
		const errors: string[] = [];

		// if (!product.categoryName) errors.push("categoryName");
		if (!product.curriculumName) errors.push("curriculumName");
		if (!product.title) errors.push("title");
		if (product.price < 0) errors.push("price");
		// if (!product.price || isNaN(product.price)) errors.push("price");

		// カテゴリーの存在確認
		// if (!categories.some(c => c.name === product.categoryName)) {
		// 	errors.push("categoryName");
		// }

		return errors;
	};

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			const data = await file.arrayBuffer();
			const workbook = XLSX.read(data);
			const worksheet = workbook.Sheets[workbook.SheetNames[0]];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const products: ExcelProduct[] = jsonData.map((row: any) => ({
				id: row['ID'],
				categoryName: '',
				curriculumName: row[i18next.t('product.curriculum_name')]
					|| row['カリキュラム名'] || row['Curriculum Name'] || row['커리큘럼명']
					|| row['教材名'] || row['Textbook Name'] || row['교재명']
					|| '',
				volume: row[i18next.t('product.volume')],
				title: row[i18next.t('common.title')],
				code: row[i18next.t('product.subject_code')],
				price: row[i18next.t('product.price')] ? Number(row[i18next.t('product.price')]) : 0,
				status: 'ACTIVE',
				categoryId: 1
			}));

			// 全商品の検証
			let hasError = false;
			products.forEach((product, index) => {
				const errors = validateProduct(product);

				product.errors = errors;
				if (errors.length > 0) {
					hasError = true;
				}
			});
			setUploadError(hasError ? i18next.t('product.upload_error') : "");

			setExcelProducts(products);
			setOpenPreviewDialog(true);
			// setUploadError('');
		} catch (error) {
			console.error('Excel file reading error:', error);
		} finally {
			if (fileInputRef.current) {
				fileInputRef.current!.value = '';
			}
		}
	};

	const handleUploadConfirm = async () => {
		viewStatusStore.setProgressStatus({
			open: true,
			progress: 0,
			total: excelProducts.length,
			messageFunc: (progress: number, total: number) => `${progress}/${total}`,
			title: i18next.t('product.importing'),
		});

		try {
			const productsToUpload = excelProducts.map(product => {
				// const category = categories.find(c => c.name === product.categoryName);
				return {
					...product,
					// categoryId: category?.id
				}
			});

			let progressed = 0;
			for (const product of productsToUpload) {
				await axios.post('/api/v1/admin/products', product);
				viewStatusStore.setProgressStatus({
					open: true,
					progress: ++progressed,
					total: productsToUpload.length,
					messageFunc: (progress: number, total: number) => `${progress}/${total}`,
					title: i18next.t('product.importing'),
				});
			}

			setOpenPreviewDialog(false);
			setExcelProducts([]);
			fetchProducts();
		} catch (error) {
			console.error('Failed to import products:', error);
			setUploadError(i18next.t('product.import_failed') + (error as Error).toString());
		} finally {
			viewStatusStore.setProgressStatus({
				open: false,
				progress: 0,
				total: 0,
				messageFunc: (progress: number, total: number) => `${progress}/${total}`,
				title: "",
			});
		}
	};

	const handleUpdateProduct = async (product: ExcelProduct) => {
		if (product.price < 0) {
			alert(i18next.t('product.price_must_be_zero_or_positive'));
			return;
		}
		try {
			await axios.put(`/api/v1/admin/products/${product.id}`, {
				categoryId: product.categoryId,
				curriculumName: product.curriculumName,
				volume: product.volume,
				title: product.title,
				price: product.price,
				code: product.code,
				status: product.status
			});

			product.categoryName = product.category.name;

			await alert(
				(<ProductSimpleTableVertical products={[product]} fontSize='0.8rem' />),
				(<> {i18next.t('common.update_success')} </>),
			);

			fetchProducts();
		} catch (error) {
			console.error('Failed to update product:', error);
			await alert(
				(<ProductSimpleTableVertical products={[product]} fontSize='0.8rem' />),
				(<> {i18next.t('common.update_failed')} </>),
			);
		}
	};

	const [selectedRowIds, setSelectedRowIds] = useState((): ReadonlySet<string | number> => new Set());
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editRows, setEditRows] = useState<Product[]>([]);
	const [collapsedCurriculums, setCollapsedCurriculums] = useState<Set<string>>(new Set());

	useEffect(() => setEditRows([...products]), [products]);

	// カリキュラム名で商品をグループ化
	const groupedEditRows = useMemo(() => {
		const groups: Record<string, Product[]> = {};
		editRows.forEach(row => {
			const key = row.curriculumName || i18next.t('product.uncategorized');
			if (!groups[key]) groups[key] = [];
			groups[key].push(row);
		});
		// 各グループ内をsortOrderでソート
		Object.values(groups).forEach(rows => {
			rows.sort((a, b) => (a.sortOrder ?? a.id) - (b.sortOrder ?? b.id));
		});
		return groups;
	}, [editRows, i18next.language]);

	const toggleCurriculumCollapse = (name: string) => {
		setCollapsedCurriculums(prev => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	const handleExpandAllCurriculums = () => setCollapsedCurriculums(new Set());
	const handleCollapseAllCurriculums = () => setCollapsedCurriculums(new Set(Object.keys(groupedEditRows)));

	// ドラッグ＆ドロップ
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
		useSensor(KeyboardSensor),
	);

	const handleDragEnd = useCallback((currName: string, event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const currRows = groupedEditRows[currName];
		if (!currRows) return;

		const oldIndex = currRows.findIndex(r => r.id === active.id);
		const newIndex = currRows.findIndex(r => r.id === over.id);
		if (oldIndex === -1 || newIndex === -1) return;

		const reordered = arrayMove(currRows, oldIndex, newIndex);

		// editRows全体を更新（該当カリキュラムの順序だけ反映）
		const newEditRows = [...editRows];
		const sortOrderBase = Math.min(...reordered.map(r => r.sortOrder || r.id));
		const updates: { id: number; sortOrder: number }[] = [];
		reordered.forEach((row, idx) => {
			const newSortOrder = sortOrderBase + idx;
			const globalIdx = newEditRows.findIndex(r => r.id === row.id);
			if (globalIdx !== -1) {
				newEditRows[globalIdx] = { ...newEditRows[globalIdx], sortOrder: newSortOrder };
			}
			updates.push({ id: row.id, sortOrder: newSortOrder });
		});
		setEditRows(newEditRows);

		// APIで保存
		axios.put('/api/v1/admin/products/sort-order', updates)
			.then(() => setSnackbar({ open: true, message: i18next.t('product.sort_order_updated'), severity: 'success' }))
			.catch(() => {
				setSnackbar({ open: true, message: i18next.t('product.sort_order_update_failed'), severity: 'error' });
				fetchProducts(); // revert
			});
	}, [groupedEditRows, editRows]);

	const getCurriculumSelectState = (rows: Product[]) => {
		const ids = rows.map(r => r.id);
		const selectedCount = ids.filter(id => selectedRowIds.has(id)).length;
		return {
			allSelected: selectedCount === ids.length && ids.length > 0,
			indeterminate: selectedCount > 0 && selectedCount < ids.length,
		};
	};

	const handleCurriculumSelect = (rows: Product[]) => {
		const ids = rows.map(r => r.id);
		const { allSelected } = getCurriculumSelectState(rows);
		const next = new Set(selectedRowIds);
		if (allSelected) {
			ids.forEach(id => next.delete(id));
		} else {
			ids.forEach(id => next.add(id));
		}
		setSelectedRowIds(next);
	};

	const handleCancelEdit = () => {
		setEditRows([...products]); // revert
		setEditingId(null);
	};

	const handleRowChange = (updated: Product) => {
		setEditRows(prev => prev.map(r => r.id === updated.id ? updated : r));
	};

	const handleSaveEdit = (row: Product) => {
		setEditingId(null);
		handleUpdateProduct(row as any);
	};

	const columns = useMemo((): SortableTableColumn<Product>[] => [
		{
			key: 'id',
			label: i18next.t('common.id'),
			width: 60,
		},
		{
			key: 'curriculumName',
			label: i18next.t('product.curriculum_name'),
			// 実データ（商品468件）の教材名は p90=16文字 / 最大20文字
			width: 270,
			// 左揃え。中央揃えだと短い値が広い列の中で浮き、縦に目で追いにくい
			align: 'left',
			renderEditCell: (row, onChange) => (
				<OutlinedInput
					fullWidth
					autoFocus
					size="small"
					value={row.curriculumName}
					onChange={(e) => onChange({ ...row, curriculumName: e.target.value })}
					sx={EDIT_INPUT_SX}
				/>
			),
		},
		{
			key: 'volume',
			label: i18next.t('product.volume'),
			// 実データの巻は p50=5文字 / p90=16文字。p90が収まる幅
			width: 200,
			align: 'left',
			renderEditCell: (row, onChange) => (
				<OutlinedInput
					fullWidth
					size="small"
					value={row.volume}
					onChange={(e) => onChange({ ...row, volume: e.target.value })}
					sx={EDIT_INPUT_SX}
				/>
			),
		},
		{
			key: 'title',
			label: i18next.t('common.title'),
			// 幅を指定しない唯一の列。table-layout: fixed では幅未指定の列が余りをまとめて吸うため、
			// 画面が広いときの余白がタイトル列に集まり、他の列は指定pxのまま詰まった状態を保つ。
			// 実データのタイトルは p50=4文字 / p90=14文字。240pxはその下限。
			minWidth: 240,
			align: 'left',
			renderEditCell: (row, onChange) => (
				<OutlinedInput
					fullWidth
					size="small"
					value={row.title}
					onChange={(e) => onChange({ ...row, title: e.target.value })}
					sx={EDIT_INPUT_SX}
				/>
			),
		},
		{
			key: 'code',
			label: i18next.t('product.subject_code'),
			// 教科コードは468件中21件しか使われず、p90=3文字。
			// ヘッダー「教科コード」＋ソートアイコンが収まる最小幅に寄せた
			width: 100,
			renderEditCell: (row, onChange) => (
				<OutlinedInput
					fullWidth
					size="small"
					value={row.code}
					onChange={(e) => onChange({ ...row, code: e.target.value })}
					sx={EDIT_INPUT_SX}
				/>
			),
		},
		{
			key: 'price',
			label: i18next.t('product.price'),
			align: 'right',
			// 編集時に7桁（1,234,567）まで折り返さず表示できる幅。
			// 内訳: セル余白16px + 入力欄の左右パディング12px + 枠線2px + 数字7桁 約50px + 余裕
			width: 110,
			renderCell: (row) => i18next.t('common.price_yen', { price: row.price.toLocaleString() }),
			renderEditCell: (row, onChange) => (
				<OutlinedInput
					fullWidth
					size="small"
					value={row.price}
					onChange={(e) => {
						const val = Number(e.target.value);
						onChange({ ...row, price: val < 0 ? 0 : (val || 0) });
					}}
					type="number"
					sx={{
						...EDIT_INPUT_SX,
						textAlign: 'right',
						// type="number" のスピナー矢印は表示幅を約16px奪うため非表示にする
						'& input[type=number]': { MozAppearance: 'textfield' },
						'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
							WebkitAppearance: 'none',
							margin: 0,
						},
					}}
					inputProps={{ min: 0, style: { textAlign: 'right' } }}
				/>
			),
		},
		{
			key: 'status',
			label: i18next.t('common.status'),
			// 「販売可能」＋プルダウン矢印が固定幅レイアウトでも収まる幅
			width: 110,
			renderCell: (row) => row.status === 'ACTIVE' ? i18next.t('common.available_for_sale') : row.status === 'INACTIVE' ? i18next.t('common.private') : i18next.t('common.status_deleted'),
			renderEditCell: (row, onChange) => (
				<Select
					fullWidth
					size="small"
					value={row.status}
					onChange={(e) => onChange({ ...row, status: e.target.value as Product['status'] })}
					sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { px: '6px' } }}
				>
					<MenuItem value="ACTIVE">{i18next.t('common.available_for_sale')}</MenuItem>
					<MenuItem value="INACTIVE">{i18next.t('common.private')}</MenuItem>
				</Select>
			),
		},
		{
			key: 'edit',
			label: i18next.t('common.edit'),
			// 編集中は「保存」「キャンセル」の2ボタンが並ぶ。固定幅レイアウトでは広がれないため確保する
			width: 120,
			sortable: false,
			renderCell: (row) => {
				if (editingId === row.id) {
					return (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
							<Button size="small" startIcon={<CheckIcon sx={{ m: 0, p: 0 }} />}
								onClick={(e) => { e.stopPropagation(); handleSaveEdit(editRows.find(r => r.id === row.id) || row); }}
								sx={{ fontSize: '0.6rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
							>
								{i18next.t('common.save')}
							</Button>
							<Button size="small" color="error" startIcon={<CloseIcon sx={{ m: 0, p: 0 }} />}
								onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
								sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
							>
								{i18next.t('common.cancel')}
							</Button>
						</Box>
					);
				}
				return (
					<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
						<Button size="small" disabled={editingId != null && editingId !== row.id}
							startIcon={<EditIcon sx={{ m: 0, p: 0 }} />}
							onClick={(e) => { e.stopPropagation(); setEditingId(row.id); }}
							sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
						>
							{i18next.t('common.edit')}
						</Button>
					</Box>
				);
			},
		},
		{
			key: 'delete',
			label: i18next.t('common.delete'),
			width: 70,
			sortable: false,
			renderCell: (row) => (
				<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					<Button size="small" color="error" startIcon={<DeleteIcon sx={{ m: 0, p: 0 }} />}
						onClick={(e) => { e.stopPropagation(); handleDelete(row as any); }}
						sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
					>
						{i18next.t('common.delete')}
					</Button>
				</Box>
			),
		},
	], [editingId, editRows, products, i18next.language]);

	const summaryRow = useMemo(() => (
		<TableRow>
			{/* drag(1) + checkbox(1) + id(1) = 3 */}
			<TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.8rem' }}>
				{i18next.t('common.total')}
			</TableCell>
			{/* curriculumName + volume + title + code + price = 5 */}
			<TableCell colSpan={5} />
			{/* status = 1 */}
			<TableCell sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.8rem' }}>
				{i18next.t('common.count_of_total_other', { count: editRows.filter(r => r.status === 'ACTIVE').length, totalCount: editRows.length })}
			</TableCell>
			{/* edit + delete = 2 */}
			<TableCell colSpan={2} />
		</TableRow>
	), [editRows, i18next.language]);

	const visibleColumns = useMemo(() => columns.filter(col => !col.hidden), [columns]);
	const totalColSpan = visibleColumns.length + 1; // +1 for checkbox

	// table-layout: fixed で列幅を固定するため、必要な最小幅を列定義から合計する。
	// 固定しないと、編集行の入力欄が入った瞬間にブラウザが全列を再配分し、
	// 「編集する行によって別の列が縮む」挙動になる。
	// 30 = ドラッグ列、40 = チェックボックス列。
	const tableMinWidth = useMemo(
		() => visibleColumns.reduce((sum, col) => sum + (Number(col.width) || Number(col.minWidth) || 100), 0) + 30 + 40,
		[visibleColumns],
	);

	const allRowsSelected = editRows.length > 0 && editRows.every(r => selectedRowIds.has(r.id));
	const someRowsSelected = editRows.some(r => selectedRowIds.has(r.id)) && !allRowsSelected;

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedRowIds(new Set(editRows.map(r => r.id)));
		} else {
			setSelectedRowIds(new Set());
		}
	};

	const handleSelectRow = (id: number) => {
		const next = new Set(selectedRowIds);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setSelectedRowIds(next);
	};

	const renderSortIcon = (columnKey: string) => {
		if (sortColumn !== columnKey) {
			return <UnfoldMoreIcon sx={{ fontSize: '1rem', ml: 0.5, opacity: 0.3 }} />;
		}
		return sortDirection === 'asc'
			? <ArrowUpwardIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
			: <ArrowDownwardIcon sx={{ fontSize: '1rem', ml: 0.5 }} />;
	};

	const handleDownloadTemplate = () => {
		const link = document.createElement('a');
		const langCode = getBrowserLanguage();
		let href: string;
		switch (langCode) {
			case 'ko':
				href = '/product_list_upload_template_ko.xlsx';
				break;
			case 'en':
				href = '/product_list_upload_template_en.xlsx';
				break;
			default:
				href = '/product_list_upload_template_ja.xlsx';
				break;
		}

		link.href = href;
		link.download = 'product_list_upload_template.xlsx';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const mobileCardFields: MobileCardField<Product>[] = [
		{ key: 'curriculumName', label: i18next.t('product.curriculum_name'), primary: true },
		{ key: 'volume', label: i18next.t('product.volume') },
		{ key: 'title', label: i18next.t('common.title') },
		{ key: 'code', label: i18next.t('product.subject_code') },
		{ key: 'price', label: i18next.t('product.price'), getValue: (row) => i18next.t('common.price_yen', { price: row.price.toLocaleString() }) },
		{ key: 'status', label: i18next.t('common.status'), getValue: (row) => row.status === 'ACTIVE' ? i18next.t('common.available_for_sale') : row.status === 'INACTIVE' ? i18next.t('common.private') : i18next.t('common.status_deleted') },
	];

	return (
		<Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
			{/* アクションボタン */}
			<Box sx={adminStyles.headerBox}>
				<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'space-between', width: '100%' }}>
					<Stack direction="row" spacing={1}>
						<Button
							variant={selectedRowIds.size > 0 ? "outlined" : "text"}
							onClick={() => setIsDeleteConfirmDialogOpen(true)}
							disabled={selectedRowIds.size === 0}
							size={isMobile ? "small" : "medium"}
							sx={{ ...adminStyles.actionButton as any, ...(isMobile && { fontSize: '0.75rem' }) }}
						>
							{i18next.t('common.delete_selected')}
						</Button>
						<Button
							variant={selectedRowIds.size > 0 ? "outlined" : "text"}
							onClick={openBulkEditDialog}
							disabled={selectedRowIds.size === 0}
							size={isMobile ? "small" : "medium"}
							sx={{ ...adminStyles.actionButton as any, ...(isMobile && { fontSize: '0.75rem' }) }}
						>
							{i18next.t('common.bulk_edit')}
						</Button>
						<Button
							variant="contained"
							startIcon={<AddIcon sx={isMobile ? { fontSize: '1rem !important' } : undefined} />}
							onClick={() => navigate('/admin/products/create')}
							size={isMobile ? "small" : "medium"}
							sx={{ ...adminStyles.actionButton as any, ...(isMobile && { fontSize: '0.75rem' }) }}
						>
							{i18next.t('common.add')}
						</Button>
					</Stack>
					<Stack direction="row" spacing={1}>
						<Button
							variant="contained"
							startIcon={<DownloadIcon sx={isMobile ? { fontSize: '1rem !important' } : undefined} />}
							onClick={handleDownloadTemplate}
							size={isMobile ? "small" : "medium"}
							sx={{ ...adminStyles.actionButton as any, ...(isMobile && { fontSize: '0.75rem' }) }}
						>
							{i18next.t('product_management.download_format')}
						</Button>
						<Button
							variant="contained"
							component="label"
							startIcon={<UploadIcon sx={isMobile ? { fontSize: '1rem !important' } : undefined} />}
							size={isMobile ? "small" : "medium"}
							sx={{ ...adminStyles.actionButton as any, ...(isMobile && { fontSize: '0.75rem' }) }}
						>
							{i18next.t('product_management.upload_file')}
							<input ref={fileInputRef} type="file" hidden accept=".xlsx" onChange={handleFileUpload} />
						</Button>
					</Stack>
				</Stack>
			</Box>

			{/* 検索・フィルター */}
			<Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.filterPaper as any, ...(isMobile && { p: 1.5, mb: 1.5, borderRadius: 0, boxShadow: 'none', border: 'none' }) }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<SearchInput
						ref={searchInputRef}
						initialValue={searchParams.keyword}
						onCommit={handleSearch}
						variant="outlined"
						size="small"
						placeholder={i18next.t('product.search_hint')}
						sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: '32px', fontSize: '0.8rem' } }}
					/>
					<Button
						variant="contained"
						size="small"
						onClick={() => handleSearch()}
						sx={{ ...adminStyles.searchButton, flexShrink: 0, fontSize: '0.8rem', height: '32px', ...(isMobile && { minWidth: 32, px: 0.5 }) }}
					>
						{isMobile ? <SearchIcon sx={{ fontSize: '1.1rem' }} /> : i18next.t('common.search')}
					</Button>
					<Stack direction="row" spacing={1} sx={{ ml: 'auto', flexShrink: 0 }}>
							<Tooltip title={i18next.t('common.expand_all')}>
								<Button
									variant="outlined"
									size="small"
									onClick={handleExpandAllCurriculums}
									sx={{ minWidth: 'unset', px: 0.5 }}
								>
									<UnfoldMoreIcon fontSize="small" />
								</Button>
							</Tooltip>
							<Tooltip title={i18next.t('common.collapse_all')}>
								<Button
									variant="outlined"
									size="small"
									onClick={handleCollapseAllCurriculums}
									sx={{ minWidth: 'unset', px: 0.5 }}
								>
									<UnfoldLessIcon fontSize="small" />
								</Button>
							</Tooltip>
						</Stack>
				</Stack>
			</Paper>

			{/* 商品リスト */}
			<Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.tablePaper as any, ...(isMobile && { borderRadius: 0, boxShadow: 'none', border: 'none', overflow: 'visible' }) }}>
				{isMobile ? (
					<Box sx={{ p: 1 }}>
						{editRows.length === 0 ? (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography variant="body2" color="text.secondary">
									データがありません
								</Typography>
							</Box>
						) : (
							Object.entries(groupedEditRows).map(([currName, currRows]) => {
								const isCollapsed = collapsedCurriculums.has(currName);
								return (
									<Box key={currName} sx={{ mb: 1 }}>
										{/* カリキュラムグループヘッダー */}
										<Paper
											elevation={0}
											sx={{
												p: 1.5,
												border: '1px solid',
												borderColor: 'primary.light',
												borderRadius: '8px',
												cursor: 'pointer',
												bgcolor: 'grey.50',
											}}
											onClick={() => toggleCurriculumCollapse(currName)}
										>
											<Box sx={{ display: 'flex', alignItems: 'center' }}>
												<Checkbox
													size="small"
													checked={getCurriculumSelectState(currRows).allSelected}
													indeterminate={getCurriculumSelectState(currRows).indeterminate}
													onClick={(e) => { e.stopPropagation(); handleCurriculumSelect(currRows); }}
													sx={{ p: '4px', mr: 0.5 }}
												/>
												<IconButton size="small" sx={{ mr: 0.5, p: '2px' }}>
													{isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
												</IconButton>
												<Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
													{currName}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{currRows.length}件
												</Typography>
											</Box>
										</Paper>
										{/* カリキュラム内商品カード（ドラッグ＆ドロップ対応） */}
										{!isCollapsed && (
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(currName, e)}>
												<SortableContext items={currRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
													{currRows.map((row) => (
														<SortableMobileCard
															key={row.id}
															row={row}
															isSelected={selectedRowIds.has(row.id)}
															onSelectRow={handleSelectRow}
															onDelete={(r) => handleDelete(r as any)}
															onNavigate={(id) => navigate(`/admin/products/edit/${id}`)}
														/>
													))}
												</SortableContext>
											</DndContext>
										)}
									</Box>
								);
							})
						)}
					</Box>
				) : (
					<TableContainer>
						<Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
							<TableHead>
								<TableRow sx={{ bgcolor: '#f8f9fa' }}>
									<TableCell sx={{ bgcolor: '#f8f9fa', width: 30, p: 0 }} />
									<TableCell padding="checkbox" sx={{ bgcolor: '#f8f9fa', fontWeight: 600, width: 40 }}>
										<Checkbox
											size="small"
											indeterminate={someRowsSelected}
											checked={allRowsSelected}
											onChange={(e) => handleSelectAll(e.target.checked)}
										/>
									</TableCell>
									{visibleColumns.map((col) => {
										const isSortable = col.sortable !== false;
										return (
											<TableCell
												key={col.key}
												align={col.align || 'center'}
												sx={{
													bgcolor: '#f8f9fa',
													fontWeight: 600,
													fontSize: '0.8rem',
													px: '8px',
													width: col.width,
													minWidth: col.minWidth,
													whiteSpace: 'nowrap',
													...(isSortable && {
														cursor: 'pointer',
														userSelect: 'none',
														'&:hover': { opacity: 0.7 },
													}),
												}}
												onClick={isSortable ? () => handleSort(col.key) : undefined}
											>
												<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.align || 'center' }}>
													{col.label}
													{isSortable && renderSortIcon(col.key)}
												</Box>
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{editRows.length === 0 ? (
									<TableRow>
										<TableCell colSpan={totalColSpan + 1} sx={{ textAlign: 'center', py: 4 }}>
											<Typography variant="body2" color="text.secondary">
												データがありません
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									Object.entries(groupedEditRows).map(([currName, currRows]) => {
										const isCollapsed = collapsedCurriculums.has(currName);
										const { allSelected: groupAllSelected, indeterminate: groupIndeterminate } = getCurriculumSelectState(currRows);
										return (
											<React.Fragment key={currName}>
												{/* カリキュラムグループヘッダー */}
												<TableRow sx={{ bgcolor: 'grey.100' }}>
													<TableCell colSpan={totalColSpan + 1} sx={{ py: 0.5, px: 1 }}>
														<Box sx={{ display: 'flex', alignItems: 'center' }}>
															<Checkbox
																size="small"
																checked={groupAllSelected}
																indeterminate={groupIndeterminate}
																onChange={() => handleCurriculumSelect(currRows)}
																sx={{ p: '4px', mr: 0.5 }}
															/>
															<IconButton
																size="small"
																onClick={() => toggleCurriculumCollapse(currName)}
																sx={{ p: '4px', mr: 0.5 }}
															>
																{isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
															</IconButton>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																{currName} ({currRows.length})
															</Typography>
														</Box>
													</TableCell>
												</TableRow>
												{/* カリキュラム内商品行（ドラッグ＆ドロップ対応） */}
												{!isCollapsed && (
													<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(currName, e)}>
														<SortableContext items={currRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
															{currRows.map((row) => (
																<SortableProductRow
																	key={row.id}
																	row={row}
																	isEditing={editingId != null && editingId === row.id}
																	isSelected={selectedRowIds.has(row.id)}
																	visibleColumns={visibleColumns}
																	onSelectRow={handleSelectRow}
																	onRowChange={handleRowChange}
																/>
															))}
														</SortableContext>
													</DndContext>
												)}
											</React.Fragment>
										);
									})
								)}
							</TableBody>
							<TableFooter>
								{summaryRow}
							</TableFooter>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<ProductUploadConfirmDialog
				open={openPreviewDialog}
				onClose={() => setOpenPreviewDialog(false)}
				uploadError={uploadError}
				excelProducts={excelProducts}
				setOpenPreviewDialog={setOpenPreviewDialog}
				handleUploadConfirm={handleUploadConfirm}
			/>

			<ConfirmDialog
				open={isDeleteFailDialogOpen}
				onClose={null}
				onConfirm={() => setIsDeleteFailDialogOpen(false)}
				title={i18next.t('common.delete_failed')}
				message={`${i18next.t('product.cannot_delete_in_set')}\n${deleteFailMessage}`}
				button1Text={null}
				button2Text={i18next.t('common.confirm')}
			/>
			<ConfirmDialog
				open={isDeleteConfirmDialogOpen}
				onClose={() => setIsDeleteConfirmDialogOpen(false)}
				onConfirm={() => { setIsDeleteConfirmDialogOpen(false); handleDeleteSelected(); }}
				title={i18next.t('common.confirm_delete')}
				message={`${selectedRowIds.size}${i18next.t('common.count_items_delete')}`}
				button1Text={i18next.t('common.cancel')}
				button2Text={i18next.t('common.delete')}
				confirmColor="error"
			/>

			{/* 一括編集ダイアログ */}
			<Dialog
				open={isBulkEditDialogOpen}
				onClose={() => setIsBulkEditDialogOpen(false)}
				maxWidth="sm"
				fullWidth
				fullScreen={isMobile}
			>
				<DialogTitle>{i18next.t('common.bulk_edit')} ({i18next.t('common.count_items_other', { count: selectedRowIds.size })})</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						{([
							{ key: 'curriculumName', label: i18next.t('product.curriculum_name') },
							{ key: 'volume', label: i18next.t('product.volume') },
							{ key: 'title', label: i18next.t('common.title') },
							{ key: 'price', label: i18next.t('product.price') },
						] as const).map(({ key, label }) => (
							<Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={bulkEditFields[key].enabled}
											onChange={(e) => setBulkEditFields(prev => ({
												...prev,
												[key]: { ...prev[key], enabled: e.target.checked }
											}))}
										/>
									}
									label={label}
									sx={{ minWidth: 120, mr: 0 }}
								/>
								<TextField
									size="small"
									fullWidth
									type={key === 'price' ? 'number' : 'text'}
									disabled={!bulkEditFields[key].enabled}
									value={bulkEditFields[key].value}
									onChange={(e) => setBulkEditFields(prev => ({
										...prev,
										[key]: { ...prev[key], value: e.target.value }
									}))}
									inputProps={key === 'price' ? { min: 0 } : undefined}
									sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
								/>
							</Box>
						))}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={bulkEditFields.status.enabled}
										onChange={(e) => setBulkEditFields(prev => ({
											...prev,
											status: { ...prev.status, enabled: e.target.checked }
										}))}
									/>
								}
								label={i18next.t('common.status')}
								sx={{ minWidth: 120, mr: 0 }}
							/>
							<Select
								size="small"
								fullWidth
								disabled={!bulkEditFields.status.enabled}
								value={bulkEditFields.status.value === i18next.t('common.mixed_values') ? '' : bulkEditFields.status.value}
								onChange={(e) => setBulkEditFields(prev => ({
									...prev,
									status: { ...prev.status, value: e.target.value }
								}))}
								displayEmpty
								sx={{ fontSize: '0.875rem' }}
							>
								{bulkEditFields.status.value === i18next.t('common.mixed_values') && (
									<MenuItem value="" disabled>{i18next.t('common.mixed_values')}</MenuItem>
								)}
								<MenuItem value="ACTIVE">{i18next.t('common.available_for_sale')}</MenuItem>
								<MenuItem value="INACTIVE">{i18next.t('common.private')}</MenuItem>
							</Select>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsBulkEditDialogOpen(false)}>
						{i18next.t('common.cancel')}
					</Button>
					<Button
						onClick={handleBulkEditSave}
						variant="contained"
						color="primary"
						disabled={!Object.values(bulkEditFields).some(f => f.enabled)}
					>
						{i18next.t('common.save')}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ソート順保存Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default ProductManagement;